import { Button, Chip, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'

import {
  fetchApartmentPayout,
  updateApartmentPayout,
  updateApartmentPayoutAccount
} from 'api/apartment'
import { Loading } from 'app/components/generic/LottieComponents'
import Table from 'app/components/generic/Table'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'
import { formatAmount } from 'app/utils'

import ApartmentFormModal from '../shared/ApartmentFormModal'
import ApartmentPageHeader from '../shared/ApartmentPageHeader'
import { useSelectedApartment } from '../shared/apartmentStore'
import MonthFilter from '../shared/MonthFilter'

const useStyles = makeStyles((theme) => ({
  // Edge-to-edge table (own horizontal scroll); only the header is padded.
  root: { minWidth: 0, overflowX: 'hidden' },
  header: { padding: theme.spacing(3, 3, 0) },
  account: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    whiteSpace: 'pre-line',
    color: theme.palette.text.secondary
  },
  accountHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}))

const tableOptions = {
  selectableRows: 'none',
  filterType: 'dropdown',
  // Match the main system: the grid is end-to-end with ONE horizontal
  // scroller of its own and a fixed header. Do not wrap it in another
  // overflow container or you get two scrollbars.
  responsive: 'vertical',
  fixedHeader: true,
  tableBodyMaxHeight: 'calc(100vh - 320px)',
  print: false,
  download: true,
  viewColumns: false,
  rowsPerPage: 25,
  rowsPerPageOptions: [10, 15, 25, 100],
  elevation: 0
}

// Only "Paid?" and "Notes" are editable; revenue/share/net are computed.
const ROW_FIELDS = [
  { name: 'paid', label: 'Paid out?', type: 'checkbox' },
  { name: 'notes', label: 'Notes', type: 'textarea' }
]
const ACCOUNT_FIELDS = [
  { name: 'accountDetails', label: 'Account Details', type: 'textarea' }
]

const ApartmentPayout = () => {
  const classes = useStyles()
  const [apartment] = useSelectedApartment()
  const [data, setData] = React.useState(null)
  const [rowEdit, setRowEdit] = React.useState(null) // index or null
  const [accountOpen, setAccountOpen] = React.useState(false)
  // '' = every month. Narrowing shows just that month's settlement row.
  const [month, setMonth] = React.useState('')

  React.useEffect(() => {
    let active = true
    setData(null)
    fetchApartmentPayout(apartment.id, month).then(
      (res) => active && setData(res)
    )
    return () => {
      active = false
    }
  }, [apartment.id, month])

  if (!data) return <Loading />

  const amountCol = (label, name) => ({
    name,
    label,
    options: { filter: false, customBodyRender: (v) => formatAmount(v) }
  })

  const columns = [
    { name: 'month', label: 'Month' },
    amountCol('Total Rev', 'totalRev'),
    amountCol(`Apartment Share (${data.sharePercent}%)`, 'apartmentShare'),
    {
      name: 'paid',
      label: 'Paid?',
      options: {
        customBodyRender: (value) => (
          <Chip
            label={value ? 'Paid' : 'Pending'}
            size="small"
            style={
              value
                ? { color: '#2e7d32', backgroundColor: '#e8f5e9' }
                : { color: '#ef6c00', backgroundColor: '#fff3e0' }
            }
          />
        )
      }
    },
    amountCol('Net Total', 'netTotal'),
    amountCol('Net Apartment', 'netApartment'),
    { name: 'notes', label: 'Notes', options: { filter: false } },
    {
      name: '',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => (
          <Button
            size="small"
            color="primary"
            onClick={() => setRowEdit(dataIndex)}
          >
            Edit
          </Button>
        )
      }
    }
  ]

  // The server owns the money: it returns the whole recomputed payload, so we
  // re-render from that rather than patching our local copy.
  const handleRowSave = async (values) => {
    const row = data.rows[rowEdit]
    try {
      const next = await updateApartmentPayout(row.uuid, {
        paid: !!values.paid,
        notes: values.notes || ''
      })
      setData(next)
      eventAlert('Payout updated', constants.SUCCESS)
      setRowEdit(null)
    } catch (error) {
      // request.js already surfaces a toast.
    }
  }

  const handleAccountSave = async (values) => {
    try {
      const next = await updateApartmentPayoutAccount(
        apartment.id,
        values.accountDetails || ''
      )
      setData(next)
      eventAlert('Account details updated', constants.SUCCESS)
      setAccountOpen(false)
    } catch (error) {
      // request.js already surfaces a toast.
    }
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <ApartmentPageHeader
          title="Apartment Payout"
          action={
            <MonthFilter
              value={month}
              onChange={setMonth}
              allowAll
              apartmentId={apartment.id}
            />
          }
        />

        <Paper variant="outlined" className={classes.account}>
          <div className={classes.accountHeader}>
            <strong>Payout account ({data.sharePercent}% revenue share)</strong>
            <Button
              size="small"
              color="primary"
              onClick={() => setAccountOpen(true)}
            >
              Edit account
            </Button>
          </div>
          {data.accountDetails}
        </Paper>
      </div>

      <Table
        data={data.rows}
        columns={columns}
        options={tableOptions}
        disableFullHeight
      />

      <ApartmentFormModal
        open={rowEdit !== null}
        fields={ROW_FIELDS}
        initialValues={rowEdit !== null ? data.rows[rowEdit] : {}}
        onSubmit={handleRowSave}
        onClose={() => setRowEdit(null)}
      />
      <ApartmentFormModal
        open={accountOpen}
        fields={ACCOUNT_FIELDS}
        initialValues={{ accountDetails: data.accountDetails }}
        onSubmit={handleAccountSave}
        onClose={() => setAccountOpen(false)}
      />
    </div>
  )
}

export default ApartmentPayout
